/* Собираем PDF прямо из готовых листов на странице.

   Идея простая: по каждому листу обходим DOM, снимаем координаты у строк текста,
   заливок и рамок — и рисуем то же самое в PDF. Текст остаётся текстом: его можно
   выделять и искать, файл получается лёгким, а вёрстка ровно та, что на экране,
   потому что позиции берём у браузера, а не считаем заново. */
import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';

// Лист рисуем ровно в A4 (595.28×841.89 пт): масштаб берём от ширины листа на
// экране, чтобы при печати ничего не подгонялось и не обрезалось.
const A4 = { w: 595.276, h: 841.89 };
const FONT_DIR = `${process.env.PUBLIC_URL || ''}/landing/fonts`;

// Какие начертания вшиваем. Ключ — семейство и вес.
const FILES = {
  'Montserrat:400': 'Montserrat-Regular.ttf',
  'Montserrat:500': 'Montserrat-Medium.ttf',
  'Montserrat:600': 'Montserrat-600.ttf',
  'Montserrat:700': 'Montserrat-Bold.ttf',
  'Montserrat:800': 'Montserrat-800.ttf',
  'Oswald:500': 'Oswald-500.ttf',
  'Oswald:600': 'Oswald-600.ttf',
};
const WEIGHTS = { Montserrat: [400, 500, 600, 700, 800], Oswald: [500, 600] };
// Запасной шрифт: в Montserrat и Oswald нет ∨ ∧ ≡ ⋅ ∈ и прочей математики —
// на экране браузер подставляет свой шрифт, в PDF подставлять нечем, и вместо
// символов получаются пустые прямоугольники. Подгружаем только при нужде.
const FALLBACK = 'DejaVuSans.ttf';

const nearest = (list, w) => list.reduce((a, b) => (Math.abs(b - w) < Math.abs(a - w) ? b : a));

const parseColor = (css) => {
  const m = String(css).match(/rgba?\(([^)]+)\)/);
  if (!m) return { color: rgb(0, 0, 0), alpha: 1 };
  const [r, g, b, a = '1'] = m[1].split(',').map(s => parseFloat(s));
  return { color: rgb(r / 255, g / 255, b / 255), alpha: parseFloat(a) };
};

// PDF понимает только PNG и JPEG — всё остальное (webp, gif, svg) перегоняем в
// PNG через canvas. Байты у нас свои (blob), так что canvas не «испорченный».
const kindOf = (buf) => {
  if (buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4e && buf[3] === 0x47) return 'png';
  if (buf[0] === 0xff && buf[1] === 0xd8) return 'jpg';
  return null;
};
async function toPng(buf) {
  const url = URL.createObjectURL(new Blob([buf]));
  try {
    const img = new Image();
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    const c = document.createElement('canvas');
    c.width = Math.max(1, img.naturalWidth); c.height = Math.max(1, img.naturalHeight);
    c.getContext('2d').drawImage(img, 0, 0);
    const blob = await new Promise(r => c.toBlob(r, 'image/png'));
    return new Uint8Array(await blob.arrayBuffer());
  } finally { URL.revokeObjectURL(url); }
}

/* Байты картинки. Логотип с CSS-фильтром прогоняем через canvas (иначе в PDF он
   останется цветным). Остальное пробуем взять напрямую, а если хост не отдаёт
   байты браузеру (CORS) — через бэкенд (proxy), который подкачивает их сам. */
async function imageBytes(img, filter, proxy) {
  if (filter && filter !== 'none') {
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(img.naturalWidth || img.width));
    c.height = Math.max(1, Math.round(img.naturalHeight || img.height));
    const ctx = c.getContext('2d');
    ctx.filter = filter;
    ctx.drawImage(img, 0, 0, c.width, c.height);
    const blob = await new Promise(r => c.toBlob(r, 'image/png'));
    return { bytes: new Uint8Array(await blob.arrayBuffer()), type: 'png' };
  }
  const src = img.currentSrc || img.src;
  let buf = null;

  /* Картинка может быть вшита прямо в условие как data:URL — тогда её не надо
     ни качать, ни проксировать: байты уже здесь. Раньше такие уходили в fetch
     и терялись, поэтому часть вариантов скачивалась без рисунков. */
  if (/^data:/i.test(src)) {
    const comma = src.indexOf(',');
    const meta = src.slice(5, comma);
    const body = src.slice(comma + 1);
    if (/;base64/i.test(meta)) {
      const bin = atob(body);
      const out = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
      buf = out;
    } else {
      buf = new TextEncoder().encode(decodeURIComponent(body));
    }
  }

  if (!buf) try {
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), 8000);
    const res = await fetch(src, { mode: 'cors', signal: ctl.signal }).finally(() => clearTimeout(t));
    if (res.ok) {
      const b = new Uint8Array(await res.arrayBuffer());
      if (b.length) buf = b;
    }
  } catch { /* хост не отдал байты браузеру — идём через бэкенд */ }
  if (!buf && proxy && !/^data:/i.test(src)) {
    try {
      const b = new Uint8Array(await proxy(src));
      if (b.length) buf = b;
    } catch { /* и прокси не смог — ниже бросим понятную ошибку */ }
  }
  if (!buf) throw new Error('картинку не получить');
  const type = kindOf(buf);
  return type ? { bytes: buf, type } : { bytes: await toPng(buf), type: 'png' };
}

/* Векторные значки (логотип в подвале) переводим в картинку: в отдельном файле
   классы из CSS уже не работают, поэтому цвета переносим прямо в атрибуты, а
   рисуем с шестикратным запасом, чтобы в печати не мылилось. */
async function svgBytes(svg) {
  const r = svg.getBoundingClientRect();
  const clone = svg.cloneNode(true);
  const src = [...svg.querySelectorAll('*')], dst = [...clone.querySelectorAll('*')];
  dst.forEach((el, i) => {
    const cs = getComputedStyle(src[i]);
    if (cs.fill && cs.fill !== 'none') el.setAttribute('fill', cs.fill);
    if (cs.stroke && cs.stroke !== 'none') el.setAttribute('stroke', cs.stroke);
  });
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', r.width);
  clone.setAttribute('height', r.height);
  const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(clone));
  const img = new Image();
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
  const S = 6;
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(r.width * S));
  c.height = Math.max(1, Math.round(r.height * S));
  c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
  const blob = await new Promise(res => c.toBlob(res, 'image/png'));
  return new Uint8Array(await blob.arrayBuffer());
}

/* Разбиваем текстовый узел на строки так, как их разложил браузер: у каждой
   строки свой прямоугольник, а границу между строками ищем двоичным поиском по
   смещению — перебирать все символы слишком медленно. */
function linesOf(node) {
  const text = node.nodeValue || '';
  if (!text.trim()) return [];
  const range = document.createRange();
  range.selectNodeContents(node);
  const rects = [...range.getClientRects()].filter(r => r.width > 0.5 && r.height > 0.5);
  if (rects.length === 0) return [];
  if (rects.length === 1) return [{ text, rect: rects[0] }];

  const topOf = (offset) => {
    const r = document.createRange();
    r.setStart(node, Math.max(0, offset));
    r.setEnd(node, Math.min(text.length, offset + 1));
    const rr = r.getClientRects()[0];
    return rr ? rr.top : null;
  };

  const out = [];
  let start = 0;
  for (let i = 0; i < rects.length - 1; i++) {
    const top = rects[i].top;
    // последний символ, который ещё стоит на этой строке
    let lo = start, hi = text.length - 1, end = start;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const t = topOf(mid);
      if (t !== null && Math.abs(t - top) < 1.5) { end = mid; lo = mid + 1; } else hi = mid - 1;
    }
    out.push({ text: text.slice(start, end + 1), rect: rects[i] });
    start = end + 1;
  }
  out.push({ text: text.slice(start), rect: rects[rects.length - 1] });
  return out.filter(l => l.text.trim());
}

export async function exportPagesToPdf(pageEls, fileName = 'Вариант.pdf', onProgress, { imageProxy } = {}) {
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);

  const cache = new Map();
  const fontFor = async (family, weight) => {
    const fam = /oswald/i.test(family) ? 'Oswald' : 'Montserrat';
    const w = nearest(WEIGHTS[fam], parseInt(weight, 10) || 400);
    const key = `${fam}:${w}`;
    if (!cache.has(key)) {
      const bytes = await (await fetch(`${FONT_DIR}/${FILES[key]}`)).arrayBuffer();
      cache.set(key, await doc.embedFont(bytes, { subset: true }));
    }
    return cache.get(key);
  };
  let fallback = null;
  const fallbackFont = async () => {
    if (!fallback) {
      const bytes = await (await fetch(`${FONT_DIR}/${FALLBACK}`)).arrayBuffer();
      fallback = await doc.embedFont(bytes, { subset: true });
    }
    return fallback;
  };
  const hasGlyph = (font, ch) => {
    const f = font.embedder?.font;
    try { return f?.hasGlyphForCodePoint ? f.hasGlyphForCodePoint(ch.codePointAt(0)) : true; }
    catch { return true; }
  };
  /* Режем строку на куски по тому, есть ли символ в основном шрифте. Обычно
     кусок один; отдельным куском уходят только редкие символы. */
  const runsOf = async (text, font) => {
    const runs = [];
    let cur = null;
    for (const ch of text) {
      const ok = ch === ' ' || hasGlyph(font, ch);
      if (!cur || cur.ok !== ok) { cur = { ok, text: '' }; runs.push(cur); }
      cur.text += ch;
    }
    if (runs.some(r => !r.ok)) {
      const fb = await fallbackFont();
      runs.forEach(r => { r.font = r.ok ? font : fb; });
    } else {
      runs.forEach(r => { r.font = font; });
    }
    return runs;
  };

  // доля высоты строки над базовой линией — по метрикам самого шрифта
  const ascentRatio = (font) => {
    const f = font.embedder?.font;
    if (!f?.ascent) return 0.8;
    return f.ascent / (f.ascent - f.descent);
  };

  for (let pi = 0; pi < pageEls.length; pi++) {
    const el = pageEls[pi];
    const pr = el.getBoundingClientRect();
    const page = doc.addPage([A4.w, A4.h]);
    const PT = A4.w / pr.width;                  // px → пункты для этого листа
    const PTY = A4.h / pr.height;
    const X = (v) => (v - pr.left) * PT;
    const Y = (v) => A4.h - (v - pr.top) * PTY;  // ось Y в PDF снизу вверх

    // белый фон листа
    page.drawRectangle({ x: 0, y: 0, width: A4.w, height: A4.h, color: rgb(1, 1, 1) });

    const walk = async (node) => {
      if (node.nodeType === 3) {                      // текст
        const cs = getComputedStyle(node.parentElement);
        if (cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return;
        const font = await fontFor(cs.fontFamily, cs.fontWeight);
        const size = parseFloat(cs.fontSize);
        const { color } = parseColor(cs.color);
        const spacing = cs.letterSpacing === 'normal' ? 0 : parseFloat(cs.letterSpacing) || 0;
        const up = cs.textTransform === 'uppercase';
        for (const line of linesOf(node)) {
          const raw = line.text.replace(/\s+/g, ' ');
          const txt = up ? raw.toUpperCase() : raw;
          const base = line.rect.top + ascentRatio(font) * line.rect.height;
          const x = X(line.rect.left), y = Y(base);
          try {
            let cx = x;
            for (const run of await runsOf(txt, font)) {
              if (spacing) {                          // разрядка — рисуем по буквам
                for (const ch of run.text) {
                  page.drawText(ch, { x: cx, y, size: size * PT, font: run.font, color });
                  cx += (run.font.widthOfTextAtSize(ch, size) + spacing) * PT;
                }
              } else {
                page.drawText(run.text, { x: cx, y, size: size * PT, font: run.font, color });
                cx += run.font.widthOfTextAtSize(run.text, size) * PT;
              }
            }
          } catch { /* символ не отрисовался — пропускаем строку, чем ронять файл */ }
        }
        return;
      }
      if (node.nodeType !== 1) return;
      const cs = getComputedStyle(node);
      if (cs.display === 'none' || cs.visibility === 'hidden') return;
      const r = node.getBoundingClientRect();

      // заливка
      const bg = parseColor(cs.backgroundColor);
      if (bg.alpha > 0.01 && r.width > 0 && r.height > 0 && node !== el) {
        page.drawRectangle({ x: X(r.left), y: Y(r.bottom), width: r.width * PT, height: r.height * PTY, color: bg.color, opacity: bg.alpha });
      }
      // рамки — рисуем как тонкие полоски
      const sides = [
        ['Top', r.left, r.top, r.width, parseFloat(cs.borderTopWidth), cs.borderTopColor],
        ['Bottom', r.left, r.bottom - parseFloat(cs.borderBottomWidth), r.width, parseFloat(cs.borderBottomWidth), cs.borderBottomColor],
        ['Left', r.left, r.top, parseFloat(cs.borderLeftWidth), r.height, cs.borderLeftColor, true],
        ['Right', r.right - parseFloat(cs.borderRightWidth), r.top, parseFloat(cs.borderRightWidth), r.height, cs.borderRightColor, true],
      ];
      for (const [, x, y, w, h, col, vert] of sides) {
        const thick = vert ? w : h;
        if (!thick || thick < 0.2) continue;
        const c = parseColor(col);
        if (c.alpha < 0.01) continue;
        const ww = vert ? thick : w, hh = vert ? h : thick;
        page.drawRectangle({ x: X(x), y: Y(y + hh), width: ww * PT, height: hh * PTY, color: c.color, opacity: c.alpha });
      }

      if (node.tagName?.toLowerCase?.() === 'svg') {
        try {
          const img = await doc.embedPng(await svgBytes(node));
          page.drawImage(img, { x: X(r.left), y: Y(r.bottom), width: r.width * PT, height: r.height * PTY });
        } catch { /* значок не отрисовался — не повод ронять файл */ }
        return;
      }
      if (node.tagName === 'IMG') {
        try {
          /* Раньше здесь стоял отказ, если картинка не отрисовалась в браузере
             (например, хост не отдал её из-за CORS) — и она просто пропадала из
             PDF. Теперь такие случаи идут через бэкенд-прокси: он скачает байты
             сам. Без источника тянуть действительно нечего. */
          const painted = node.complete && node.naturalWidth > 0;
          if (!painted && !(imageProxy && (node.currentSrc || node.src))) throw new Error('нет картинки');
          const { bytes, type } = await imageBytes(node, painted ? cs.filter : 'none', imageProxy);
          const img = type === 'png' ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
          page.drawImage(img, { x: X(r.left), y: Y(r.bottom), width: r.width * PT, height: r.height * PTY });
        } catch {
          // картинку не отдали (нет CORS) — оставляем рамку, чтобы место не поехало
          page.drawRectangle({ x: X(r.left), y: Y(r.bottom), width: r.width * PT, height: r.height * PTY, borderColor: rgb(0.85, 0.83, 0.9), borderWidth: 0.5 });
        }
        return;
      }
      for (const child of node.childNodes) await walk(child);
    };

    await walk(el);
    if (onProgress) onProgress(pi + 1, pageEls.length);
  }

  const bytes = await doc.save();
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return blob.size;
}
